import React, { useMemo, useState } from 'react';
import {
	CircularProgress,
	IconButton,
	Menu,
	MenuItem,
	TableCell,
	TableRow,
	Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import moment from 'moment-timezone';
import { InvoiceWithStoreDetails, InvoiceTableStrings, ExtraColumn } from './InvoiceTable.types';
import classNames from 'classnames';
import { RiErrorWarningLine, RiMoreFill } from 'react-icons/ri';
import { getPricingString, notNull } from 'common/utils/common';
import BackdropDialog from '../BackdropDialog';
import { Callable } from 'common/frontend/callable';
import { getCurrencySymbolFromCurrencyCode } from 'common/utils/currencyUtils';

interface Props {
	invoice: InvoiceWithStoreDetails;
	strings: InvoiceTableStrings;
	showAdminActions?: boolean;
	onError?: (err: string) => void;
	extraColumns?: ExtraColumn[];
}

interface InvoiceAction {
	key:
		| 'download-receipt'
		| 'download-invoice'
		| 'edit'
		| 'cancel'
		| 'charge'
		| 'refund'
		| 'mark-as-paid'
		| 'mark-as-refunded';
	label: string;
	handler: () => Promise<void>;
	prompt: {
		label: string;
		body: JSX.Element;
	} | null;
	admin: boolean;
	statuses: Array<InvoiceWithStoreDetails['status']>;
}

const InvoiceTableRow = (props: Props) => {
	const classes = useStyles();
	const { invoice, strings, showAdminActions } = props;

	const currencyCode = invoice.totals.currency;
	const invoiceCurrencySymbol = getCurrencySymbolFromCurrencyCode(currencyCode);

	const [loading, setLoading] = useState<boolean>(false);
	const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<HTMLElement | null>(null);
	const [pendingAction, setPendingAction] = useState<InvoiceAction | null>(null);

	const actions: InvoiceAction[] = useMemo(() => {
		return [
			{
				key: 'download-receipt',
				label: strings.download,
				handler: async () => {
					try {
						const url = await Callable.billing.invoices.getUrl({
							invoiceId: invoice.id,
							type: 'receipt',
						});
						if (!!url) {
							window.open(url, '_blank');
						}
					} catch (err) {
						throw new Error(strings.downloadFailed);
					}
				},
				admin: false,
				prompt: null,
				statuses: ['paid'],
			},
			{
				key: 'download-invoice',
				label: strings.download,
				handler: async () => {
					try {
						const url = await Callable.billing.invoices.getUrl({
							invoiceId: invoice.id,
							type: 'invoice',
						});
						if (!!url) {
							window.open(url, '_blank');
						}
					} catch (err) {
						throw new Error(strings.downloadFailed);
					}
				},
				admin: false,
				prompt: null,
				statuses: ['pending', 'failed', 'refunded'],
			},
			{
				/**
				 * Only visible in internal admin, no need to localize
				 */
				key: 'edit',
				label: 'Edit',
				handler: async () =>
					Callable.billing.invoices.cancel({ invoiceId: invoice.id, unassignEvents: true }),
				admin: true,
				prompt: {
					label: 'Edit invoice?',
					body: (
						<Typography variant="body1">
							<strong>This will:</strong>
							<ul>
								<li>
									Return the related events to the "Upcoming Invoices" tab, from where they can be
									used to create a new invoice, or may be automatically included in the next
									invoice.
								</li>
								<li>
									Mark the invoice status as <strong>cancelled</strong>
								</li>
							</ul>
							The invoice will still be visible to the merchant as "cancelled", but they can only
							see the sum of the invoice (not the breakdown).
							<br />
							<br />
							You <strong>should</strong> do this if:
							<ul>
								<li>You want to edit the invoice amount (add custom fees or discounts)</li>
							</ul>
							You <strong>should not</strong> do this if:
							<ul>
								<li>You simply want the invoice to be cancelled (use "Cancel" for that)</li>
							</ul>
						</Typography>
					),
				},
				statuses: ['pending', 'failed'],
			},
			{
				/**
				 * Only visible in internal admin, no need to localize
				 */
				key: 'cancel',
				label: 'Cancel',
				handler: async () =>
					Callable.billing.invoices.cancel({ invoiceId: invoice.id, unassignEvents: false }),
				admin: true,
				prompt: {
					label: 'Cancel invoice?',
					body: (
						<Typography variant="body1">
							<strong>This will:</strong>
							<ul>
								<li>
									Mark the invoice status as <strong>cancelled</strong>.
								</li>
								<li>
									Keep the related billing events attached to this invoice. They will effectively be
									refunded (will be ignored in any future invoices).
								</li>
							</ul>
							The invoice will still be visible to the merchant as "cancelled", but they can only
							see the sum of the invoice (not the breakdown).
							<br />
							<br />
							You <strong>should</strong> do this if:
							<ul>
								<li>
									You want to cancel the invoice without any intention of charging it in the future
								</li>
							</ul>
							You <strong>should not</strong> do this if:
							<ul>
								<li>
									You still wish to charge the invoice, but wish to make some changes to it first
									(use "Edit" for that)
								</li>
							</ul>
						</Typography>
					),
				},
				statuses: ['pending', 'failed'],
			},
			{
				/**
				 * Only visible in internal admin, no need to localize
				 */
				key: 'charge',
				label: 'Charge now',
				handler: async () => Callable.billing.invoices.charge({ invoiceId: invoice.id }),
				admin: true,
				prompt: {
					label: 'Charge invoice now?',
					body: (
						<Typography variant="body1">
							<strong>This will:</strong>
							<ul>
								<li>
									Attempt to charge the invoice amount{' '}
									<strong>
										({getPricingString(invoice?.totals.total ?? 0, invoiceCurrencySymbol)})
									</strong>{' '}
									from the customers credit card
								</li>
								<li>
									If the payment is successful, mark the invoice status as <strong>paid</strong>
								</li>
								<li>
									If the payment is not successful, mark the invoice status as{' '}
									<strong>failed</strong>
								</li>
							</ul>
							<br />
							<br />
							You <strong>should</strong> do this if:
							<ul>
								<li>
									You want to immediately charge the invoice, without waiting for the next billing
									date
								</li>
								<li>You want to retry a previously failed payment</li>
							</ul>
						</Typography>
					),
				},
				statuses: ['pending', 'failed'],
			},
			{
				/**
				 * Only visible in internal admin, no need to localize
				 */
				key: 'refund',
				label: 'Refund',
				handler: async () => Callable.billing.invoices.refund({ invoiceId: invoice.id }),
				admin: true,
				prompt: {
					label: 'Refund invoice',
					body: (
						<Typography variant="body1">
							<strong>This will:</strong>
							<ul>
								<li>Refund any payment that has been made towards the invoice</li>
								<li>Refund any credits that have been used towards the invoice</li>
								<li>
									Mark the invoice status as <strong>refunded</strong>
								</li>
							</ul>
							<br />
							<br />
							You <strong>should</strong> do this if:
							<ul>
								<li>You want to cancel and refund the invoice</li>
							</ul>
							You <strong>should not</strong> do this if:
							<ul>
								<li>The invoice was paid via an external system (e.g. Procountor)</li>
								<li>The invoice payment has already been refunded via Adyen</li>
								<li>
									You wish to refund the invoice as Rentle credits instead of actual money (in that
									case: use "Mark as refunded" and manually add credits for the merchant)
								</li>
							</ul>
							In the above cases, the refund will fail - if you want to mark the invoices as
							refunded, use "Mark as refunded".
						</Typography>
					),
				},
				statuses: ['paid'],
			},
			{
				/**
				 * Only visible in internal admin, no need to localize
				 */
				key: 'mark-as-paid',
				label: 'Mark as paid',
				handler: async () => Callable.billing.invoices.markPaid({ invoiceId: invoice.id }),
				admin: true,
				prompt: {
					label: 'Mark invoice as paid?',
					body: (
						<Typography variant="body1">
							<strong>This will:</strong>
							<ul>
								<li>
									Mark the invoice status as <strong>paid</strong>
								</li>
								<li>
									<strong>Not charge</strong> the customer's credit card
								</li>
							</ul>
							The invoice amount will be included in billing reports in the same way as if it were
							charged from the customer's credit card, and the merchant will see the invoice status
							as "paid" as well as be able to download the receipt for the invoice.
							<br />
							<br />
							You <strong>should</strong> do this if:
							<ul>
								<li>
									The invoice payment has been received some other way (e.g. manual invoicing)
								</li>
							</ul>
							You <strong>should not</strong> do this if:
							<ul>
								<li>The invoice payment has not yet been received</li>
								<li>You simply want to cancel the invoice (use "cancel" for that)</li>
							</ul>
						</Typography>
					),
				},
				statuses: ['pending', 'failed'],
			},
			{
				/**
				 * Only visible in internal admin, no need to localize
				 */
				key: 'mark-as-refunded',
				label: 'Mark as refunded',
				handler: async () => Callable.billing.invoices.markRefunded({ invoiceId: invoice.id }),
				admin: true,
				prompt: {
					label: 'Mark invoice as refunded?',
					body: (
						<Typography variant="body1">
							<strong>This will:</strong>
							<ul>
								<li>
									Mark the invoice status as <strong>refunded</strong>
								</li>
								<li>
									<strong>Not refund</strong> any payments that have been made
								</li>
							</ul>
							<br />
							<br />
							You <strong>should</strong> do this if:
							<ul>
								<li>
									The invoice payment has already been refunded (via Adyen, Procountor or some other
									means)
								</li>
							</ul>
							You <strong>should not</strong> do this if:
							<ul>
								<li>The invoice payment has not yet been refunded</li>
								<li>
									You wish to also refund the invoice payment automatically (use "Refund" for that)
								</li>
							</ul>
						</Typography>
					),
				},
				statuses: ['paid'],
			},
		];
	}, [invoice, invoiceCurrencySymbol, strings.download, strings.downloadFailed]);

	const handleAction = (action: InvoiceAction) => async () => {
		if (!!action.prompt && pendingAction?.key !== action.key) {
			setPendingAction(action);
		} else {
			setLoading(true);

			try {
				await action.handler();
				setActionMenuAnchorEl(null);
			} catch (err) {
				props.onError?.(err.message);
			}
			setLoading(false);
		}
	};

	const renderStatus = (invoice: InvoiceWithStoreDetails) => {
		switch (invoice.status) {
			case 'cancelled':
				return (
					<Typography className={classNames(classes.status, classes.statusCancelled)}>
						{strings.statusCancelled}
					</Typography>
				);
			case 'pending':
				return (
					<Typography className={classNames(classes.status, classes.statusPending)}>
						{strings.statusPending}
					</Typography>
				);
			case 'paid':
				return (
					<Typography className={classNames(classes.status, classes.statusPaid)}>
						{strings.statusPaid}
					</Typography>
				);
			case 'failed':
				return (
					<Typography className={classNames(classes.status, classes.statusFailed)}>
						{strings.statusFailed} <RiErrorWarningLine color="inherit" size={18} />
					</Typography>
				);
			case 'refunded':
				return (
					<Typography className={classNames(classes.status, classes.statusRefunded)}>
						{strings.statusRefunded}
					</Typography>
				);
		}
	};

	const renderActions = (invoice: InvoiceWithStoreDetails) => {
		if (loading) {
			return (
				<IconButton disabled size="large">
					<CircularProgress color="primary" size={20} />
				</IconButton>
			);
		}

		const actionsToRender = actions
			.map((action) => {
				if (action.admin && !showAdminActions) return null;
				if (!action.statuses.includes(invoice.status)) return null;
				return (
					<MenuItem key={action.key} onClick={handleAction(action)}>
						{action.label}
					</MenuItem>
				);
			})
			.filter(notNull);

		if (actionsToRender.length === 0) return null;

		return (
			<>
				<IconButton
					aria-controls={`${invoice.id}_menu`}
					aria-haspopup="true"
					onClick={(e) => setActionMenuAnchorEl(e.currentTarget)}
					size="large"
				>
					<RiMoreFill />
				</IconButton>
				<Menu
					id={`${invoice.id}_menu`}
					anchorEl={actionMenuAnchorEl}
					keepMounted={false}
					open={Boolean(actionMenuAnchorEl)}
					onClose={() => setActionMenuAnchorEl(null)}
					classes={{
						paper: classes.rowActionMenu,
					}}
				>
					{actionsToRender}
				</Menu>
			</>
		);
	};

	const renderActionPrompt = (pendingAction: InvoiceAction | null) => {
		if (!pendingAction || !pendingAction.prompt) return null;

		return (
			<BackdropDialog
				variant="danger"
				isOpen
				onClose={() => setPendingAction(null)}
				label={pendingAction.prompt.label}
				body={pendingAction.prompt.body}
				confirm={{
					label: 'Confirm',
					callback: async () => {
						await handleAction(pendingAction)();
						setPendingAction(null);
					},
				}}
				cancel={{
					label: 'Go back',
					callback: () => setPendingAction(null),
				}}
			/>
		);
	};

	const getBillingCycleString = (invoice: InvoiceWithStoreDetails) =>
		!!invoice.billingCycle
			? `${moment(invoice.billingCycle.start).format('MMM Do')} - ${moment(invoice.billingCycle.end)
					.subtract(1, 'day')
					.format('MMM Do, YYYY')}`
			: moment
					.utc()
					.year(invoice.date.year)
					.month(invoice.date.month - 1)
					.format('MMMM YYYY');

	return (
		<>
			<TableRow className={classes.tableRow} key={invoice.id}>
				<TableCell align="left">{getBillingCycleString(invoice)}</TableCell>
				<TableCell align="left">{moment(invoice.createdDate).format('MMM Do, YYYY')}</TableCell>
				{props.extraColumns?.includes('merchantName') && (
					<TableCell align="left">{invoice.customer.name}</TableCell>
				)}
				<TableCell>{invoice.storeName}</TableCell>
				<TableCell align="left">{invoice.invoiceNumber}</TableCell>
				<TableCell align="left">{renderStatus(invoice)}</TableCell>
				<TableCell align="right">
					{getPricingString(invoice.totals.total, invoiceCurrencySymbol)}
				</TableCell>
				<TableCell align="right">{renderActions(invoice)}</TableCell>
			</TableRow>
			{renderActionPrompt(pendingAction)}
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		tableRow: {
			borderBottom: `1px solid ${theme.palette.border.table}`,
			height: '82px',
		},
		status: {
			fontSize: 14,
		},
		statusPending: {
			color: theme.palette.text.primary,
		},
		statusPaid: {
			color: theme.palette.success.main,
		},
		statusFailed: {
			color: theme.palette.error.main,
		},
		statusCancelled: {
			color: theme.palette.text.secondary,
		},
		statusRefunded: {
			color: theme.palette.text.secondary,
		},
		rowActionMenu: {
			width: '200px',
			maxWidth: '100vw',
		},
	}),
);

export default InvoiceTableRow;
